import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Lock, Download, RefreshCw, LogOut, Inbox, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const TYPES = ["all", "assessment", "franchise", "membership", "contact"];

const badge = {
  assessment: "bg-[#c9a14a]/15 text-[#c9a14a] border-[#c9a14a]/40",
  franchise: "bg-emerald-500/10 text-emerald-400 border-emerald-500/40",
  membership: "bg-sky-500/10 text-sky-400 border-sky-500/40",
  contact: "bg-purple-500/10 text-purple-300 border-purple-500/40",
};

export default function AdminEnquiries() {
  const [key, setKey] = useState(sessionStorage.getItem("carlton_admin_key") || "");
  const [input, setInput] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);

  const load = async (k) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/enquiries`, { headers: { "X-Admin-Key": k } });
      setRows(data);
      sessionStorage.setItem("carlton_admin_key", k);
      setKey(k);
    } catch {
      sessionStorage.removeItem("carlton_admin_key");
      setKey("");
      toast.error("Invalid access key");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => r.type === filter)),
    [rows, filter]
  );

  const exportCsv = () => {
    const cols = ["created_at", "type", "name", "phone", "email", "city", "message", "source_page"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...filtered.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `carlton-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteRow = async (row) => {
    const ok = window.confirm(
      `Delete this enquiry?\n\n${row.name} · ${row.type}\n${row.email}\n\nThis action cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await axios.delete(`${API}/api/enquiries/${row.id}`, { headers: { "X-Admin-Key": key } });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry");
    } finally {
      setDeletingId(null);
    }
  };

  if (!key) {
    return (
      <main data-testid="admin-login" className="min-h-screen bg-[#070604] pt-[72px] md:pt-20 flex items-center justify-center px-5">
        <div className="w-full max-w-sm border border-[#c9a14a]/30 bg-[#0b0b0b] p-10">
          <span className="w-12 h-12 rounded-full border border-[#c9a14a]/50 flex items-center justify-center text-[#c9a14a]">
            <Lock size={18} strokeWidth={1.5} />
          </span>
          <h1 className="mt-6 font-serif-display text-3xl text-[#f7f2e8]">Team Access</h1>
          <p className="mt-2 text-[13px] text-[#f7f2e8]/60">
            Enter the private access key to view enquiries.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (input.trim()) load(input.trim());
            }}
            className="mt-7 space-y-4"
          >
            <Input
              type="password"
              data-testid="admin-key-input"
              placeholder="Access key"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="bg-transparent border-[#c9a14a]/40 text-[#f7f2e8] rounded-none focus-visible:ring-0 focus-visible:border-[#c9a14a]"
            />
            <button type="submit" data-testid="admin-login-button" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60">
              {loading ? "Checking…" : "Unlock Dashboard"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#070604] pt-[100px] md:pt-28 pb-20 px-5 md:px-10">
      <div className="max-w-[1480px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="overline text-[#c9a14a]">Team Dashboard</p>
            <h1 className="mt-3 font-serif-display text-4xl text-[#f7f2e8]">
              Enquiries <span className="font-numeric text-3xl text-[#c9a14a]">({filtered.length})</span>
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => load(key)} data-testid="admin-refresh-button" className="btn-outline-gold !py-2.5 !px-4">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={exportCsv} data-testid="admin-export-button" className="btn-gold !py-2.5 !px-4">
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem("carlton_admin_key");
                setKey("");
                setRows([]);
              }}
              data-testid="admin-logout-button"
              className="btn-outline-gold !py-2.5 !px-4"
            >
              <LogOut size={14} /> Lock
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-type-filters">
          {TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={`admin-filter-${t}`}
              className={`px-4 py-2 text-[11px] font-bold tracking-[0.16em] uppercase border transition-colors ${
                filter === t
                  ? "bg-[#c9a14a] text-[#050505] border-[#c9a14a]"
                  : "border-[#c9a14a]/30 text-[#f7f2e8]/70 hover:border-[#c9a14a]/70"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-6 border border-[#c9a14a]/25 overflow-x-auto">
          <table className="w-full text-left min-w-[900px]" data-testid="admin-enquiries-table">
            <thead>
              <tr className="bg-[#0b0b0b] text-[10px] font-bold tracking-[0.2em] uppercase text-[#c9a14a]">
                {["Date", "Type", "Name", "Phone", "Email", "City", "Message", "Page", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-4 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-[#f7f2e8]/8 text-[13px] text-[#f7f2e8]/85 hover:bg-[#c9a14a]/5">
                  <td className="px-4 py-3.5 whitespace-nowrap text-[#f7f2e8]/60">
                    {new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-block px-2.5 py-1 text-[10px] font-bold tracking-[0.14em] uppercase border ${badge[r.type] || badge.contact}`}>
                      {r.type}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 font-semibold text-[#f7f2e8]">{r.name}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap">{r.phone}</td>
                  <td className="px-4 py-3.5">{r.email}</td>
                  <td className="px-4 py-3.5">{r.city}</td>
                  <td className="px-4 py-3.5 max-w-[280px] truncate" title={r.message}>{r.message}</td>
                  <td className="px-4 py-3.5 text-[#f7f2e8]/55">{r.source_page}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <button
                      onClick={() => deleteRow(r)}
                      disabled={deletingId === r.id}
                      data-testid={`admin-delete-${r.id}`}
                      aria-label="Delete enquiry"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold tracking-[0.14em] uppercase border border-red-500/40 text-red-400 hover:bg-red-500/10 hover:border-red-500 transition-colors disabled:opacity-50"
                    >
                      <Trash2 size={12} strokeWidth={1.8} />
                      {deletingId === r.id ? "Deleting…" : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center text-[#f7f2e8]/50">
                    <Inbox size={28} className="mx-auto text-[#c9a14a]/60" />
                    <p className="mt-3 text-sm">No enquiries yet.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
